import { FiChevronRight } from 'react-icons/fi';

/**
 * Default mobile asset-group header row (expand/collapse affordance).
 *
 * Public interface: group identity + progress counts (+ optional `onClick` /
 * `expanded`). Hides the chevron/progress pill layout used on mobile session
 * group lists.
 *
 * Props:
 *   groupCode — primary title (e.g. "DB.1")
 *   assetType — secondary line (e.g. "Distribution Board")
 *   location — right-side location text (e.g. "B00B R0001")
 *   testedCount — completed count for the pill
 *   totalCount — total assets for the pill
 *   expanded — when true, chevron rotates down
 *   onClick — optional; renders as a button when provided
 *   className — extra class(es) on the root
 */
export default function MobileAssetGroupDefault({
  groupCode,
  assetType,
  location,
  testedCount = 0,
  totalCount = 0,
  expanded = false,
  onClick,
  className = '',
  ...props
}) {
  const progress = `${testedCount}/${totalCount}`;
  const pillState = totalCount > 0 && testedCount === totalCount
    ? 'complete'
    : testedCount > 0
      ? 'partial'
      : 'empty';
  const classes = [
    'mobile-asset-group',
    expanded ? 'mobile-asset-group--expanded' : '',
    className,
  ].filter(Boolean).join(' ');

  const body = (
    <>
      <span className="mobile-asset-group__lead">
        <FiChevronRight
          size={18}
          className="mobile-asset-group__chevron"
          aria-hidden
        />
        <span className="mobile-asset-group__identity">
          <span className="mobile-asset-group__code">{groupCode}</span>
          {assetType ? (
            <span className="mobile-asset-group__type">{assetType}</span>
          ) : null}
        </span>
      </span>

      <span className="mobile-asset-group__meta">
        {location ? (
          <span className="mobile-asset-group__location">{location}</span>
        ) : null}
        <span className={`mobile-asset-group__progress mobile-asset-group__progress--${pillState}`}>
          {progress}
        </span>
      </span>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={classes}
        onClick={onClick}
        aria-expanded={expanded}
        {...props}
      >
        {body}
      </button>
    );
  }

  return (
    <div className={classes} {...props}>
      {body}
    </div>
  );
}
